import {
  type WhiteboardSessionData,
  type RawContentAPI,
  type WhiteboardQueryParams,
  type OnboardingStartHandler,
  type RawFrameToken,
  type ContainerHandler,
  type RawTokenHandler,
  UserTypes,
  type MoreActions,
  type RouteHandler,
  type ConfluenceSidebarState,
} from '@canvas/frame-api-types';
import { unlockAllPaywallsResponseMock } from '@canvas/paywall-test-utils';
import { noop, noopAsync } from '@canvas/utils';

// This is a real board ID from PUG. It ensures that any calls that are made to
// the real world will success in environments in integration tests and
// yarn start --mode=offline
// Once all endpoints are mocked you we can use a fake UUID like 'bada555e-1337-1337-1337-427778884200'
export const mockBoardUuid = '67cf0f6d-d456-4a0c-94f6-1ecc75b04506';
const conatinerId = '1767342081';

const extractQueryParams = (query?: WhiteboardQueryParams): URLSearchParams => {
  const queryParams = query ?? {};
  const queryStringified = Object.fromEntries(
    Object.entries(queryParams).map(([k, v]) => [k, `${v}`]),
  );

  // TODO - fix passing of query params after routing changes land
  const params = new URLSearchParams(queryStringified);
  void params;

  return params;
};

export class NoopContentAPI implements RawContentAPI {
  private tokenChangeHandler: RawTokenHandler | undefined;
  private routeChangeHandler: RouteHandler | undefined;

  public sessionData: WhiteboardSessionData = {
    accountId: '655363:155223cf-ec56-4228-82a1-9a1a4aba851f',
    tenantId: 'DUMMY-a5a01d21-1cc3-4f29-9565-f2bb8cd969f5',
    activationId: '0591fa3a-8f53-4cfe-8f24-2f7be5d4011b', // PUG confluence activation id from snoopr
    publicLinkUserType: UserTypes.CONFLUENCE_USER,
  };

  public goToContainer = noop;
  public goToHome = noop;

  protected getQueryParams(query: WhiteboardQueryParams | undefined) {
    const providedParams = extractQueryParams(query);

    return Object.fromEntries(providedParams as any);
  }
  public goToContent = async (id: string, query?: WhiteboardQueryParams) => {
    const token = await this.getToken();

    const params = new URLSearchParams(this.getQueryParams(query));

    // Preserve the current URL path prefix (e.g. /slide/ vs /whiteboard/)
    const prefix = globalThis.location?.pathname?.startsWith('/slide/')
      ? 'slide'
      : 'whiteboard';
    this.routeChangeHandler?.(
      `/${prefix}/${id}${params.size > 0 ? `?${params.toString()}` : ''}`,
    );
    this.tokenChangeHandler?.(token);
  };
  public onRouteChange = (handler: (route: string) => void) => {
    this.routeChangeHandler = handler;
  };

  public onContainerChange = noop;
  public onContentLoadSucceeded = noop;
  public onContentLoadFailed = noop;
  public onContentLoadAborted = noop;
  public setNavigationLockoutState = noop;

  public isFeatureActive = () => true;
  public registerStartOnboardingCallback: (cb: OnboardingStartHandler) => void = noop;
  public onOnboardingChange = noop;
  public onOnboardingEnd = noop;

  public updateCollaborators = noop;
  public updateMainActions = noop;
  public updateMoreActions = (_items: MoreActions) => {
    // no-op
  };
  public updateBoardStatus = noop;
  public setTabFullscreen = noop;

  public refreshToken = noopAsync;
  public preloadToken = noopAsync;
  public preloadContent = noopAsync;

  public onTokenInit = async (_handler: RawTokenHandler) => {};
  public onTokenChange = (handler: RawTokenHandler) => {
    this.tokenChangeHandler = handler;
  };
  public onContainerInit = (_handler: ContainerHandler) => {};

  public getToken = async (): Promise<RawFrameToken> => ({
    // Just some mock data from our staging
    container: `ari:cloud:confluence:DUMMY-a5a01d21-1cc3-4f29-9565-f2bb8cd969f5:space/${conatinerId}`,
    board: `ari:cloud:canvas:DUMMY-a5a01d21-1cc3-4f29-9565-f2bb8cd969f5:board/${mockBoardUuid}`,
    // Note: Could use AsapTokenPageObject if not for cyclic dependencies.
    // string taken from that directly
    accessToken:
      'eyJhbGciOiJSUzI1NiIsImtpZCI6Im1pY3Jvcy1ncm91cC9jb25mbHVlbmNlL2xvY2FsLWtleSJ9.eyJhY2Nlc3MiOnsiYXJpOmNsb3VkOmNhbnZhczpEVU1NWS1hNWEwMWQyMS0xY2MzLTRmMjktOTU2NS1mMmJiOGNkOTY5ZjU6Ym9hcmQvNjdjZjBmNmQtZDQ1Ni00YTBjLTk0ZjYtMWVjYzc1YjA0NTA2IjpbInJlYWQiLCJ3cml0ZSIsImFkZC9jb21tZW50IiwiZGVsZXRlL2NvbW1lbnQiXX0sInNpdGVJZCI6IkRVTU1ZLWE1YTAxZDIxLTFjYzMtNGYyOS05NTY1LWYyYmI4Y2Q5NjlmNSIsImFjdGl2YXRpb25JZCI6IkRVTU1ZLWFjdGl2YXRpb24taWQiLCJpc0V4dGVybmFsIjpmYWxzZSwic3ViIjoiYXJpOmNsb3VkOmlkZW50aXR5Ojp1c2VyLzY1NTM2MzoxNTUyMjNjZi1lYzU2LTQyMjgtODJhMS05YTFhNGFiYTg1MWYiLCJpc3MiOiJtaWNyb3MtZ3JvdXAvY29uZmx1ZW5jZSIsImF1ZCI6ImNhbnZhcy1zZXJ2ZXIiLCJpYXQiOjE3NDE4NzkwNDYsIm5iZiI6MTc0MTg3OTA0NiwiZXhwIjoxNzQxODgyNjQ2LCJqdGkiOiIxNDZlOGJlMGQ1NmI3MDg3NTg3NmJjOTg4NWRmNTAxYjllYTljNGMwIn0.eLzm_ir5G1S-26jvZZHZ9bSKckoKOVXPxG0f0yGl_2EQ0y_cmZi6z_wiyTsKIF571lM3rNoSjyGDVly86VUvPzOjlGzpQyjmQSqWhFXb3ihJ0gfsh58uS8FrId7_ahp-L8l6jx4XNpz16Sjskj6xQF1j1Yv_ZM50RO4wHQyqTZvaTyP0ugOAZsR8ofpCU9nyJHhXAjLuehYeN0GC-cnmjCzyZ-P115ByjtOo9owiwd9rIpPPguMB3AToa-FCIxHCqke3hsuyENsCxK5IzcOl7-2ru738oOCVFMc4ZLZBJzfVaSgkdjnTataSORMVIU2JTj2jPL9Vi_GqncW9rmNIdQ',
    mediaToken: '',
    mediaBaseUrl: 'https://media.staging.atl-paas.net',
    mediaCollectionId: '454196199437',
    features: unlockAllPaywallsResponseMock,
    policies: {
      dataSecurityPolicy: {
        pageExport: true,
      },
    },
  });

  public showEditionUpgradeCTA = noopAsync;

  public closeEditionUpgradeCTA = noop;

  public onThemeChange = noop;

  public onOnboardingReady = noop;

  public registerEventHandler = noop;
  public unregisterEventHandler = noop;
  public triggerIntegration = noopAsync;
  public checkIntegration = () => false;

  public isExternalShareRequest = false;
  public getPublicLink = () => ({
    isEnabled: false,
    isEditEnabled: false,
    refetch: noop,
  });
  public refetchPublicLink = noop;

  public getContentCreatorId = () => null;

  public toggleAIAgentRightPanel = noop;

  public getConfluenceSidebarState = (): ConfluenceSidebarState => ({
    isOpen: false,
    panelId: undefined,
  });

  public getAIAgentDialogPosition = () => ({
    toggleBottom: -1,
    toggleMiddle: -1,
  });

  public confluenceAPI = {
    comments: {
      registerOnReopenResolvedComment: noop,
    },
    onboarding: {
      notifyShareCriteriaProgress: noop,
    },
  };
}
